import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, Utensils, ShoppingBag, Camera, Phone, Hospital, Shield } from "lucide-react";

const PLACES = [
  { t: "Lalbagh Botanical Garden", c: "240-acre botanical wonder with the iconic glass house.", d: "8 km from venue" },
  { t: "Cubbon Park", c: "Historic 300-acre park in the city center.", d: "1 km from venue" },
  { t: "Bengaluru Palace", c: "Tudor-style royal residence inspired by Windsor Castle.", d: "5 km from venue" },
  { t: "Vidhana Soudha", c: "Iconic seat of Karnataka legislature, Neo-Dravidian architecture.", d: "1.2 km" },
  { t: "ISKCON Temple", c: "Stunning hilltop temple complex.", d: "11 km" },
  { t: "Tipu Sultan's Summer Palace", c: "Indo-Islamic teak palace from 1791.", d: "6 km" }
];
const FOOD = [
  { t: "MTR (Mavalli Tiffin Room)", c: "Iconic South Indian breakfast since 1924. Try the Rava Idli." },
  { t: "Vidyarthi Bhavan", c: "Legendary masala dosa, 80+ years old in Basavanagudi." },
  { t: "Karavalli", c: "Award-winning coastal Indian fine dining at The Gateway Hotel." },
  { t: "Toit Brewpub", c: "Bengaluru's craft beer pioneer, great wood-fired pizzas." },
  { t: "Truffles", c: "Best burgers in town — a 30+ year favorite." },
  { t: "Koshy's", c: "Classic Bengaluru cafe since 1940, perfect for breakfast." }
];
const SHOPPING = [
  { t: "Commercial Street", c: "Bengaluru's oldest shopping street — clothing, jewelry, souvenirs." },
  { t: "UB City Mall", c: "Premium luxury brands in a glass-tower setting." },
  { t: "Brigade Road", c: "Lifestyle shopping hub with international brands." },
  { t: "MG Road Boulevard", c: "Open-air walking street with shops and cafes." }
];
const EMERGENCY = [
  { t: "Police Emergency", c: "100", icon: Shield },
  { t: "Ambulance", c: "108", icon: Hospital },
  { t: "Fire", c: "101", icon: Shield },
  { t: "Manipal Hospital (24/7)", c: "+91 80 2502 4444", icon: Hospital },
  { t: "Tourist Helpline", c: "1363", icon: Phone },
  { t: "AAM 2026 Help Desk", c: "+91 80 4000 2026", icon: Phone }
];

const Section = ({ icon: Icon, title, items, eyebrow }) => (
  <section className="py-12">
    <div className="flex items-center gap-3 mb-2"><Icon className="w-5 h-5 text-[#00F0FF]" /><div className="eyebrow">{eyebrow}</div></div>
    <h2 className="font-display font-black text-3xl md:text-4xl mb-6">{title}</h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((p, i) => (
        <div key={i} className="card-dark p-5" data-testid={`city-${eyebrow}-${i}`}>
          <div className="font-display font-bold text-lg">{p.t}</div>
          <div className="text-sm text-[#94A3B8] mt-1">{p.c}</div>
          {p.d && <div className="text-xs text-[#00F0FF] mt-2">{p.d}</div>}
        </div>
      ))}
    </div>
  </section>
);

export default function CityGuide() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="relative h-80 overflow-hidden pt-16">
        <img src="https://images.pexels.com/photos/14845309/pexels-photo-14845309.jpeg" alt="bengaluru" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1429] via-[#0B1429]/60 to-transparent" />
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="eyebrow mb-3">Explore</div>
            <h1 className="font-display font-black text-5xl md:text-6xl">Welcome to <span className="text-[#00F0FF]">Bengaluru</span></h1>
            <p className="text-[#CBD5E1] mt-2">Garden city, tech capital and your home for AAM 2026.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {user && <BackButton className="mb-6" />}

        {/* History of Bengaluru */}
        <section className="py-8" id="history">
          <div className="flex items-center gap-3 mb-2"><MapPin className="w-5 h-5 text-[#00F0FF]" /><div className="eyebrow">History</div></div>
          <h2 className="font-display font-black text-3xl md:text-4xl mb-6">The story of <span className="text-[#00F0FF]">Bengaluru</span></h2>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="card-dark p-6 space-y-4 text-[#CBD5E1] text-sm md:text-base leading-relaxed">
              <p>
                Founded in <strong className="text-white">1537</strong> by the chieftain Kempegowda I, Bengaluru began as a fortified town with a mud fort, four gates, and bustling markets called <em>pete</em>s — many of which still exist today as commercial neighbourhoods.
              </p>
              <p>
                The city flourished under the <strong className="text-white">Wodeyars of Mysore</strong> and <strong className="text-white">Hyder Ali & Tipu Sultan</strong>, who built the famous Bengaluru Fort and Summer Palace. After the fall of Tipu Sultan in 1799, the British established a Cantonment that brought parks, churches, and broad boulevards — earning Bengaluru the title of <strong className="text-[#00F0FF]">"Garden City"</strong>.
              </p>
              <p>
                Post-independence, Bengaluru became home to India's premier scientific institutions — ISRO, HAL, IISc — earning it the title of <strong className="text-[#00F0FF]">"India's Silicon Valley"</strong>. Today it's one of the world's fastest-growing tech metros and a vibrant cosmopolitan hub.
              </p>
              <p>
                At <strong className="text-white">920 m above sea level</strong>, Bengaluru enjoys one of India's most pleasant climates — perfect for hosting world-class international sporting events like the AMSC 2026.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fact n="1537" l="Year Founded" />
              <Fact n="920m" l="Elevation" />
              <Fact n="13.6M" l="Population" />
              <Fact n="3rd" l="Largest Indian City" />
              <Fact n="800+" l="IT Companies" />
              <Fact n="20-30°C" l="Year-round Temp" />
            </div>
          </div>
        </section>

        <Section icon={Camera} eyebrow="Tourist Places" title="Must-visit attractions" items={PLACES} />
        <Section icon={Utensils} eyebrow="Food & Drink" title="Iconic Bengaluru restaurants" items={FOOD} />
        <Section icon={ShoppingBag} eyebrow="Shopping" title="Where to shop" items={SHOPPING} />

        <section className="py-12">
          <div className="flex items-center gap-3 mb-2"><MapPin className="w-5 h-5 text-[#00F0FF]" /><div className="eyebrow">Map</div></div>
          <h2 className="font-display font-black text-3xl md:text-4xl mb-6">Find your way around</h2>
          <div className="h-[420px] rounded-xl overflow-hidden border border-white/10">
            <iframe title="Bengaluru Map" src="https://www.google.com/maps?q=Bangalore,Karnataka,India&output=embed" className="w-full h-full" style={{ filter: "invert(0.92) hue-rotate(180deg) brightness(0.9)" }} />
          </div>
        </section>

        <section className="py-12">
          <div className="flex items-center gap-3 mb-2"><Phone className="w-5 h-5 text-[#00F0FF]" /><div className="eyebrow">Emergency</div></div>
          <h2 className="font-display font-black text-3xl md:text-4xl mb-6">Important contacts</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {EMERGENCY.map((e, i) => (
              <div key={i} className="card-dark p-5 flex items-center gap-3" data-testid={`emergency-${i}`}>
                <div className="w-10 h-10 bg-[#00F0FF]/10 rounded flex items-center justify-center"><e.icon className="w-5 h-5 text-[#00F0FF]" /></div>
                <div>
                  <div className="font-semibold text-sm">{e.t}</div>
                  <div className="text-[#00F0FF] font-mono text-sm">{e.c}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

const Fact = ({ n, l }) => (
  <div className="card-dark p-4 text-center">
    <div className="font-display font-black text-2xl text-[#00F0FF]">{n}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1">{l}</div>
  </div>
);
